/**
 * Предложение включить напоминания — с обещанием звёзд.
 *
 * Показывается КАЖДЫЙ РАЗ, когда человек открывает карту дня, пока напоминания
 * не включены. Так решил владелец, и решение записано здесь, чтобы его не
 * «починили» позже как навязчивость.
 *
 * **Но есть случай, когда окно не показывается, и это не смягчение правила, а
 * его исполнимость.** Системный запрос разрешения бывает ровно один раз: если
 * человек уже отказал, `Notification.permission` навсегда `denied`, и
 * `requestPermission()` больше ничего не покажет. Кнопка «Включить» в таком
 * окне физически ничего не сделает — включить можно только в настройках
 * браузера, куда мы человека отправить не можем. Показывать кнопку, которая
 * заведомо не работает, — это не настойчивость, а поломка.
 *
 * Поэтому условие: разрешение ещё не спрошено (`default`). Тем, кто отказал,
 * остаётся переключатель в профиле — он честно объясняет, что нужно сделать.
 *
 * **Анониму то же окно показывает предложение войти, а не молчит.** Раньше он
 * не видел ничего: награду ему не начислят (деньги живут только на настоящих
 * аккаунтах), а обещание, которое не сбудется, хуже молчания. Но анонимов у нас
 * две трети — то есть предложение не доходило до большинства, и «включите
 * уведомления» оставалось разговором с меньшинством. Теперь вход — первая
 * половина того же подарка, и обе суммы названы сразу.
 */
import { useState } from 'react'
import { createPortal } from 'react-dom'

import { enablePush, pushSupported } from '../lib/push'
import { api } from '../lib/api'
import { haptic } from '../lib/telegram'
import { useSirr } from '../store'

export function NotifyOffer({
  bonus,
  toLogin = false,
  onLogin,
  onGranted,
  onClose,
}: {
  /** Сколько дают за напоминания. 0 — обещать нечего. */
  bonus: number
  /**
   * Сколько дают за вход. Больше нуля — окно превращается в предложение войти:
   * анониму включать напоминания за звёзды бессмысленно, ему их не начислят.
   */
  /**
   * Звать войти, а не включать напоминания.
   *
   * Явным признаком, а НЕ по размеру награды, как было раньше. Прежнее
   * `toLogin = login > 0` держалось на том, что за вход дают звёзды; когда
   * награду обнулили (за вход теперь дают первое гадание), анониму показалась
   * бы просьба про уведомления — а она ему ничего не даёт, звёзд за них
   * анониму тоже не начисляют.
   */
  toLogin?: boolean
  onLogin?: () => void
  /** Начислено: экран скажет об этом отдельным окном. */
  onGranted: (stars: number, balance?: number) => void
  onClose: () => void
}) {
  const { t, update } = useSirr()
  const [busy, setBusy] = useState(false)

  // Обещать нечего ни за вход, ни за напоминания — окна нет вовсе.
  if (!toLogin && bonus <= 0) return null

  const enable = async () => {
    if (busy) return
    setBusy(true)
    haptic.impact('medium')
    try {
      const res = await enablePush()
      if (!res.ok) {
        // Отказал в системном окне — молча закрываем. Объяснять «вы отказали»
        // человеку, который только что отказал, незачем.
        onClose()
        return
      }
      await api.setNotify(true)
      update({ notify: true })
      onGranted(res.bonus, res.stars)
    } catch {
      onClose()
    } finally {
      setBusy(false)
    }
  }

  /*
    Порталом в <body>, а не на месте, — как `CardZoom` и `RewardModal`.

    Экран появляется с анимацией `fu`, и `fill-mode: both` оставляет на `.screen`
    `transform` навсегда. Элемент с `transform` — своя система отсчёта для
    `position: fixed` и свой слой: `z-index: 60` замыкался внутри `.app__body`
    (слой 2), а нижняя панель с главной кнопкой — слой 3. Окно оказывалось ПОД
    кнопкой, подложка её не затемняла, и нажатие туда, где нарисовано «Позже»,
    попадало по «Сделать расклад». Замерено в браузере на боевом.
  */
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 28,
        background: 'rgba(9, 5, 18, 0.82)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 340,
          textAlign: 'center',
          border: '1px solid var(--line-strong)',
          borderRadius: 20,
          padding: '26px 22px',
          background: 'rgba(24, 14, 38, 0.98)',
        }}
      >
        <div style={{ fontSize: 34, color: 'var(--gold-bright)', lineHeight: 1 }}>✦</div>
        <div
          style={{
            fontFamily: 'var(--serif)',
            fontStyle: 'italic',
            fontSize: 21,
            color: 'var(--ink)',
            marginTop: 10,
            lineHeight: 1.25,
          }}
        >
          {toLogin ? t('loginBonusTitle') : t('notifyBonusTitle', { n: bonus })}
        </div>
        <div style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink-muted)', marginTop: 10 }}>
          {toLogin ? t('loginBonusBody') : t('notifyBonusBody')}
          {/* Вторую половину подарка называем, только если напоминания человеку
              вообще можно предложить: отказавшему системе включать их нечем. */}
          {toLogin && bonus > 0 && <> {t('loginBonusThen', { n: bonus })}</>}
        </div>

        <button
          type="button"
          onClick={toLogin ? onLogin : () => void enable()}
          disabled={busy}
          style={{
            marginTop: 18,
            width: '100%',
            background: 'var(--gold-gradient)',
            color: '#1a1020',
            border: 'none',
            borderRadius: 14,
            padding: '13px 20px',
            fontFamily: 'var(--sans)',
            fontSize: 15.5,
            fontWeight: 600,
            opacity: busy ? 0.6 : 1,
          }}
        >
          {toLogin ? t('enter') : t('notifyAskBtn')}
        </button>
        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 10,
            width: '100%',
            background: 'transparent',
            color: 'var(--ink-faint)',
            border: 'none',
            padding: '10px 20px',
            fontFamily: 'var(--sans)',
            fontSize: 14,
          }}
        >
          {t('notifyLater')}
        </button>
      </div>
    </div>,
    document.body,
  )
}

/**
 * Можно ли вообще предлагать: поддержка есть, разрешение ещё не спрашивали.
 *
 * Вынесено наружу, потому что решает это экран (он знает, открыл ли человек
 * карту дня), а знание про `default` живёт здесь.
 */
export function canOfferNotify(): boolean {
  return pushSupported() && Notification.permission === 'default'
}
